import { useState, useEffect, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { X, Download } from 'lucide-react'
import { Button } from './common'

const markdownComponents = {
  h1: ({ children }) => <h1 className="text-2xl font-bold mt-6 mb-3 pb-2 border-b border-gray-200">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-bold mt-5 mb-2 pb-1.5 border-b border-gray-100">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-bold mt-4 mb-2">{children}</h3>,
  h4: ({ children }) => <h4 className="text-base font-semibold mt-3 mb-1.5">{children}</h4>,
  h5: ({ children }) => <h5 className="text-sm font-semibold mt-3 mb-1">{children}</h5>,
  h6: ({ children }) => <h6 className="text-sm font-semibold mt-3 mb-1 text-gray-500">{children}</h6>,
  p: ({ children }) => <p className="text-sm leading-relaxed mb-3 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-6 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  code: ({ inline, children }) =>
    inline ? (
      <code className="bg-gray-100 text-purple-700 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
    ) : (
      <code>{children}</code>
    ),
  pre: ({ children }) => (
    <pre className="bg-slate-800 text-slate-100 rounded-lg p-4 text-xs font-mono overflow-x-auto mb-3 leading-relaxed">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-3">
      <table className="min-w-full text-sm border border-gray-300 rounded">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }) => <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold">{children}</th>,
  td: ({ children }) => <td className="border border-gray-300 px-3 py-2 text-sm">{children}</td>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-primary-300 pl-4 italic text-gray-500 my-3">{children}</blockquote>
  ),
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-700">
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-gray-200" />,
  input: ({ checked, ...props }) => (
    <input type="checkbox" checked={checked} readOnly className="mr-1.5 accent-primary-600" {...props} />
  ),
}

export default function MarkdownPreviewModal({ isOpen, onClose, fileUrl, fileName }) {
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchMarkdown = useCallback(async () => {
    if (!fileUrl) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(fileUrl)
      if (!res.ok) throw new Error(`Failed to load file (${res.status})`)
      const text = await res.text()
      setContent(text)
    } catch (err) {
      setError(err.message || 'Failed to load markdown file')
    } finally {
      setLoading(false)
    }
  }, [fileUrl])

  useEffect(() => {
    if (isOpen && fileUrl) fetchMarkdown()
    return () => { setContent(''); setError(null) }
  }, [isOpen, fileUrl, fetchMarkdown])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-6" onClick={onClose}>
      <div className="bg-white rounded-xl border border-gray-200 w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate flex-1 mr-4">{fileName || 'Markdown Preview'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors" title="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {loading && (
            <div className="space-y-3 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/2" />
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-3/4" />
              <div className="h-20 bg-gray-100 rounded w-full mt-4" />
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-2/5" />
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-sm text-red-600 mb-3">{error}</p>
              <Button variant="secondary" size="small" onClick={fetchMarkdown}>Retry</Button>
            </div>
          )}

          {!loading && !error && content && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {content}
            </ReactMarkdown>
          )}

          {!loading && !error && !content && isOpen && (
            <p className="text-sm text-gray-400 italic">This file appears to be empty.</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <Button variant="secondary" size="small" onClick={onClose}>Close</Button>
          <Button variant="primary" size="small" onClick={() => fileUrl && window.open(fileUrl, '_blank')}>
            <Download size={14} className="mr-1.5" />Download
          </Button>
        </div>
      </div>
    </div>
  )
}
